import { useFormikContext } from "formik";
import { BillUpdateOptionsModal } from "./BillUpdateOptionsModal";
import { UpdatePayload } from "store/apis/vendorBills";
import { useToast } from "hooks/useToast";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  useExtractBillFromImageMutation,
  useUploadBillMutation,
} from "store/apis/vendorBills";
import { BackendError } from "types/utils/error";
import { useRef } from "react";

type BillUpdateOptionsModalContentProps = {
  isOpen: boolean;
  close: () => void;
  tempBillFile: File[];
  billId: string;
  onModalClose: () => void;
};

export const BillUpdateOptionsModalContent = ({
  isOpen,
  close,
  tempBillFile,
  billId,
  onModalClose,
}: BillUpdateOptionsModalContentProps) => {
  const { setValues } = useFormikContext<UpdatePayload>();
  const { alertToast, successToast } = useToast();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [uploadBill, uploadBillStates] = useUploadBillMutation();
  const [extractBill, extractState] = useExtractBillFromImageMutation();
  const extractBillMutation = useRef<{ abort: () => void }>(null);

  const onClose = () => {
    extractBillMutation.current?.abort();
    onModalClose();
    close();
  };

  const onUploadBill = async () => {
    try {
      await uploadBill({
        billId,
        entityId,
        groupId,
        bill_document: tempBillFile[0],
      }).unwrap();
      successToast({ message: "Bill uploaded successfully" });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError)?.data?.error?.message,
        },
        error as Error
      );
    }
  };

  const onReplaceBill = async () => {
    if (!tempBillFile.length) return;
    try {
      const result = extractBill({
        entityId,
        groupId,
        billId,
        files: tempBillFile,
      });

      extractBillMutation.current = result;

      const data = await result.unwrap();

      // Update form values with extracted data
      setValues(data);

      successToast({ message: "Bill data replaced successfully" });
    } catch (error) {
      if ((error as Error).name === "AbortError") return;

      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <BillUpdateOptionsModal
      isOpen={isOpen}
      close={onClose}
      onReplaceBill={onReplaceBill}
      onUploadBill={onUploadBill}
      isLoading={uploadBillStates.isLoading || extractState.isLoading}
    />
  );
};
